/**
 * Combined campaigns hook — merges Voluum and Binom in parallel.
 * Each row carries `provider`, so the campaign page can route to /voluum/* or /binom/*.
 * useAllCampaigns stays Voluum + Binom (it also feeds the tag-rule pickers); the
 * by-provider reads below also serve Tres Commas (/tres-commas/*, read-only).
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMemo } from 'react';

import { idsByTracker, pickTracker } from '@/hooks/campaign-tracker/tracker-dispatch';
import { useTresCommasCampaign, useTresCommasCampaignFlows } from '@/hooks/tres-commas/useTresCommasCampaign';

import {
  useCampaigns as useVoluumCampaigns,
  useCampaign as useVoluumCampaign,
  useCampaignFlows as useVoluumCampaignFlows,
  type Campaign,
  type CampaignDetail,
  type CampaignProvider,
  type Flow,
} from './voluum/useVoluum';
import {
  useBinomCampaign,
  useBinomCampaignFlows,
  useBinomCampaigns,
} from './binom';

interface CombinedListResult {
  data: Campaign[];
  isLoading: boolean;
  error: unknown;
}

export function useAllCampaigns(): CombinedListResult {
  const voluum = useVoluumCampaigns();
  const binom = useBinomCampaigns();
  const data = useMemo(() => {
    const merged: Campaign[] = [
      ...(voluum.data ?? []),
      ...(binom.data ?? []),
    ];
    return [...merged].sort((a, b) => a.name.localeCompare(b.name));
  }, [voluum.data, binom.data]);
  return {
    data,
    isLoading: voluum.isLoading || binom.isLoading,
    error: voluum.error ?? binom.error,
  };
}

interface CampaignByProviderResult {
  data: CampaignDetail | undefined;
  isLoading: boolean;
  error: unknown;
}

/** Only the hook of the tracker `provider` names gets the id; the others stay off. */
export function useCampaignByProvider(
  campaignId: string,
  provider: CampaignProvider,
): CampaignByProviderResult {
  const ids = idsByTracker(provider, campaignId);
  const voluum = useVoluumCampaign(ids.voluum);
  const binom = useBinomCampaign(ids.binom);
  const tresCommas = useTresCommasCampaign(ids['tres-commas']);
  return pickTracker<CampaignByProviderResult>(provider, {
    voluum: { data: voluum.data, isLoading: voluum.isLoading, error: voluum.error },
    binom: { data: binom.data, isLoading: binom.isLoading, error: binom.error },
    'tres-commas': {
      data: tresCommas.data ?? undefined,
      isLoading: tresCommas.isLoading,
      error: tresCommas.error,
    },
  });
}

interface CampaignFlowsByProviderResult {
  data: Flow[] | undefined;
  isLoading: boolean;
  error: unknown;
}

export function useCampaignFlowsByProvider(
  campaignId: string,
  provider: CampaignProvider,
): CampaignFlowsByProviderResult {
  const ids = idsByTracker(provider, campaignId);
  const voluum = useVoluumCampaignFlows(ids.voluum);
  const binom = useBinomCampaignFlows(ids.binom);
  const tresCommas = useTresCommasCampaignFlows(ids['tres-commas']);
  return pickTracker<CampaignFlowsByProviderResult>(provider, {
    voluum: { data: voluum.data, isLoading: voluum.isLoading, error: voluum.error },
    binom: { data: binom.data, isLoading: binom.isLoading, error: binom.error },
    'tres-commas': {
      data: tresCommas.data,
      isLoading: tresCommas.isLoading,
      error: tresCommas.error,
    },
  });
}
